import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/app-notifications`;
export const NOTIFICATIONS_CHANGED = 'officium-notifications-changed';
export const notificationConfig = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const announceChange = () => window.dispatchEvent(new Event(NOTIFICATIONS_CHANGED));

export const useActivityNotifications = filter => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const request = useRef(0);
  const pageCount = useRef(1);
  const mutationBusy = useRef(false);

  const load = useCallback(async (pages = pageCount.current) => {
    const sequence = ++request.current;
    setRefreshing(true);
    try {
      const params = filter === 'unread' ? { unread_only: true } :
        filter === 'all' ? {} : { category: filter };
      const responses = await Promise.all(Array.from({ length: pages }, (_, index) =>
        axios.get(API, { ...notificationConfig(), params: { ...params, limit: 50, offset: index * 50 } })));
      if (sequence !== request.current) return false;
      const items = responses.flatMap(response => response.data);
      setNotifications([...new Map(items.map(item => [item.id, item])).values()]);
      setHasMore(responses[responses.length - 1].data.length === 50);
      pageCount.current = pages;
      setError('');
      announceChange();
      return true;
    } catch (err) {
      if (sequence === request.current) setError('Notifications could not load. Please try again.');
      return false;
    } finally {
      if (sequence === request.current) { setLoading(false); setRefreshing(false); }
    }
  }, [filter]);

  useEffect(() => {
    pageCount.current = 1;
    setNotifications([]);
    setLoading(true);
    setHasMore(false);
    load(1);
    const timer = setInterval(() => { if (!mutationBusy.current) load(); }, 30000);
    return () => { clearInterval(timer); request.current += 1; };
  }, [load]);

  const mutate = async (kind, id) => {
    if (mutationBusy.current) return false;
    mutationBusy.current = true;
    setBusyId(id || 'all');
    request.current += 1;
    try {
      if (kind === 'delete') await axios.delete(`${API}/${encodeURIComponent(id)}`, notificationConfig());
      else await axios.patch(id ? `${API}/${encodeURIComponent(id)}/read` : `${API}/read-all`, {}, notificationConfig());
      setNotifications(previous => kind === 'delete' ? previous.filter(item => item.id !== id) :
        previous.map(item => !id || item.id === id ? { ...item, read: true } : item)
          .filter(item => filter !== 'unread' || !item.read));
      announceChange();
      if (!id) toast.success('All notifications marked as read');
      await load();
      return true;
    } catch (err) {
      toast.error(kind === 'delete' ? 'Could not delete notification. Please try again.' : 'Could not mark notifications as read. Please try again.');
      return false;
    } finally {
      mutationBusy.current = false;
      setBusyId(null);
      setRefreshing(false);
    }
  };

  return { notifications, loading, refreshing, error, hasMore, busyId,
    refresh: () => load(), loadMore: () => load(pageCount.current + 1),
    markRead: id => mutate('read', id), markAllRead: () => mutate('read'),
    remove: id => mutate('delete', id) };
};